import { Brain, Search, FileText, Check, AlertCircle, ExternalLink } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import type { ResearchProgress, ResearchSource } from "@/services/deepResearchService";

interface DeepResearchProgressProps {
  progress: ResearchProgress;
  isActive: boolean;
}

function getProgressPercentage(progress: ResearchProgress): number {
  const { phase } = progress;

  // Map server progress phases to progress percentages
  if (phase === "research") return 10;
  if (phase === "search") return 20;
  if (phase === "sources") return 40;
  if (phase === "analyzing") return 60;
  if (phase === "synthesis") return 80;
  if (phase === "complete" || phase === "done") return 100;
  if (phase === "error") return 0;

  // Fallback for legacy phases
  if (phase === "Planning") return 5;
  if (phase === "Synthesizing") return 90;
  if (phase === "Complete" || phase === "Done") return 100;
  if (phase === "Error") return 0;

  return 10; // Default
}

export function DeepResearchProgress({ progress, isActive }: DeepResearchProgressProps) {
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const progressPercent = getProgressPercentage(progress);
  
  if (!isActive && progress.type !== "error") return null;

  const isError = progress.type === "error";
  const isComplete = progress.type === "complete" || progress.type === "done" || progress.type === "synthesis";

  return (
    <div className={`rounded-lg border p-4 space-y-3 ${isError ? "border-destructive bg-destructive/5" : "border-primary/20 bg-primary/5"}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-full ${isError ? "bg-destructive/10 text-destructive" : isComplete ? "bg-green-500/10 text-green-600" : "bg-primary/10 text-primary"}`}>
          {isError ? (
            <AlertCircle className="h-5 w-5" />
          ) : isComplete ? (
            <Check className="h-5 w-5" />
          ) : (
            <Brain className="h-5 w-5 animate-pulse" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">
              {isError ? "Research Failed" : "Deep Research"}
            </span>
            {!isError && !isComplete && (
              <Badge variant="secondary" className="text-xs">
                {progress.phase}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {isError ? progress.error : progress.message}
          </p>
        </div>
      </div>

      {!isError && (
        <>
          <Progress value={progressPercent} className="h-2" />
          
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              {progress.currentCycle !== undefined && progress.totalCycles !== undefined && (
                <span className="flex items-center gap-1">
                  <Search className="h-3 w-3" />
                  Cycle {progress.currentCycle}/{progress.totalCycles}
                </span>
              )}
              {progress.queriesExecuted !== undefined && (
                <span className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  {progress.queriesExecuted} queries
                </span>
              )}
              {progress.sourcesFound !== undefined && (
                <span className="flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" />
                  {progress.sourcesFound} sources
                </span>
              )}
            </div>
            <span>{Math.round(progressPercent)}%</span>
          </div>

          {progress.sources && progress.sources.length > 0 && (
            <Collapsible open={sourcesOpen} onOpenChange={setSourcesOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between text-xs h-7">
                  <span>View collected sources ({progress.sources.length})</span>
                  <ChevronDown className={`h-3 w-3 transition-transform ${sourcesOpen ? "rotate-180" : ""}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ScrollArea className="h-32 mt-2">
                  <div className="space-y-1 pr-3">
                    {progress.sources.map((source, idx) => (
                      <SourceItem key={`${source.url}-${idx}`} source={source} index={idx + 1} />
                    ))}
                  </div>
                </ScrollArea>
              </CollapsibleContent>
            </Collapsible>
          )}
        </>
      )}
    </div>
  );
}

function SourceItem({ source, index }: { source: ResearchSource; index: number }) {
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-2 p-1.5 rounded hover:bg-accent/50 transition-colors group text-xs"
    >
      <span className="text-muted-foreground font-mono w-4 shrink-0">[{index}]</span>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate group-hover:text-primary transition-colors">
          {source.title}
        </p>
        {source.snippet && (
          <p className="text-muted-foreground line-clamp-1">{source.snippet}</p>
        )}
      </div>
      <ExternalLink className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
    </a>
  );
}
